<template>
  <div>
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold">สินค้า</h1>
      <router-link to="/admin/products/create" class="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark text-center">
        เพิ่มสินค้า
      </router-link>
    </div>

    <!-- Mobile Card View -->
    <div class="block sm:hidden space-y-4">
      <div v-for="product in products" :key="product.id" class="bg-white rounded-lg shadow p-4">
        <div class="flex items-start gap-3 mb-3">
          <img class="h-16 w-16 rounded object-cover flex-shrink-0" :src="getProductImage(product)" alt="">
          <div class="flex-1 min-w-0">
            <p class="font-medium text-gray-900 truncate">{{ product.name }}</p>
            <p class="text-sm text-gray-500">{{ product.price }}</p>
            <p class="text-sm text-gray-500">คงเหลือ: {{ product.stock_quantity }}</p>
          </div>
          <span class="px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0" :class="product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
            {{ product.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน' }}
          </span>
        </div>
        <div class="flex gap-2">
          <router-link :to="`/admin/products/${product.id}/edit`" class="flex-1 text-center text-primary hover:text-primary-dark text-sm font-medium py-2 border border-primary rounded">
            แก้ไข
          </router-link>
          <button @click="deleteProduct(product.id)" class="flex-1 text-center text-red-600 hover:text-red-900 text-sm font-medium py-2 border border-red-300 rounded">
            ลบ
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop Table View -->
    <div class="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อสินค้า</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ราคา</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">คงเหลือ</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="product in products" :key="product.id">
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                      <div class="h-10 w-10 flex-shrink-0">
                          <img class="h-10 w-10 rounded-full object-cover" :src="getProductImage(product)" alt="">
                      </div>
                      <div class="ml-4">
                          <div class="text-sm font-medium text-gray-900">{{ product.name }}</div>
                      </div>
                  </div>
              </td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ product.price }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ product.stock_quantity }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" v-if="product.is_active">
                  ใช้งาน
                </span>
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" v-else>
                  ไม่ใช้งาน
                </span>
              </td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                <router-link :to="`/admin/products/${product.id}/edit`" class="text-primary hover:text-primary-dark mr-4">แก้ไข</router-link>
                <button @click="deleteProduct(product.id)" class="text-red-600 hover:text-red-900">ลบ</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Pagination 
        :current-page="pagination.current_page" 
        :last-page="pagination.last_page" 
        @page-change="fetchProducts" 
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';
import Pagination from '../../components/Pagination.vue';
import { getImageUrl } from '../../utils/imageUrl';

const products = ref([]);
const pagination = ref({ current_page: 1, last_page: 1 });
const { confirmDelete, showSuccess, showError, handleApiError } = useNotification();

onMounted(async () => {
    fetchProducts();
});

const fetchProducts = async (page = 1) => {
    try {
        const response = await api.get(`/admin/products?page=${page}`);
        products.value = response.data.data.data;
        pagination.value = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page
        };
    } catch (error) {
        showError('ไม่สามารถโหลดข้อมูลสินค้าได้');
    }
};

const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
        return getImageUrl(product.images[0].image_path);
    }
    return 'https://placehold.co/100x100';
};

const deleteProduct = async (id) => {
    const isConfirmed = await confirmDelete('สินค้านี้');

    if (isConfirmed) {
        try {
            // Check if product has orders
            const checkResponse = await api.get(`/admin/products/${id}/check-orders`);
            
            if (checkResponse.data.has_orders) {
                showError('ไม่สามารถลบสินค้านี้ได้เนื่องจากมีอยู่ในประวัติการสั่งซื้อ กรุณาปิดใช้งานสินค้าแทน');
                return;
            }
            
            await api.delete(`/admin/products/${id}`);
            showSuccess('ลบสินค้าเรียบร้อยแล้ว');
            fetchProducts(pagination.value.current_page);
        } catch (error) {
            handleApiError(error);
        }
    }
};
</script>
